import axios, { AxiosError } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { sendError } from '../sendError';
import { CommonRootState } from '../combineReducers';
import { ITosConsent } from '../interfaces/userConsent';

const fetchUserEulaConsentRequest = () =>
  ({
    type: 'FETCH_EULA_CONSENT_REQUEST'
  } as const);

const fetchUserEulaConsentSuccess = (payload: boolean) =>
  ({
    type: 'FETCH_EULA_CONSENT_SUCCESS',
    payload
  } as const);

const fetchUserEulaConsentFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: 'FETCH_EULA_CONSENT_FAILURE',
    error
  } as const;
};

export const fetchUserEulaConsent = (
  userId?: string
): ThunkAction<Promise<boolean | undefined>, CommonRootState, null, Actions> => async dispatch => {
  return new Promise(async (resolve, reject) => {
    try {
      dispatch(fetchUserEulaConsentRequest());

      const response = await axios.get<boolean>(`/api/v3/eula/${userId}/hasAcceptedEula`);

      dispatch(fetchUserEulaConsentSuccess(response.data));
      resolve(response.data);
    } catch (error) {
      dispatch(fetchUserEulaConsentFailure(error));
      reject();
    }
  });
};

const fetchUserTosConsentRequest = () =>
  ({
    type: 'FETCH_TOS_CONSENT_REQUEST'
  } as const);

const fetchUserTosConsentSuccess = (payload: ITosConsent) =>
  ({
    type: 'FETCH_TOS_CONSENT_SUCCESS',
    payload
  } as const);

const fetchUserTosConsentFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: 'FETCH_TOS_CONSENT_FAILURE',
    error
  } as const;
};

const forceShowTosConsent = () =>
  ({
    type: 'FORCE_SHOW_TOS_CONSENT'
  } as const);

export const fetchUserTosConsent = (
  organizationId?: string
): ThunkAction<Promise<boolean | undefined>, CommonRootState, null, Actions> => async dispatch => {
  return new Promise(async (resolve, reject) => {
    try {
      dispatch(fetchUserTosConsentRequest());

      const response = await axios.get<ITosConsent>(`/api/v1/OrganizationTermsOfService/${organizationId}`);

      dispatch(fetchUserTosConsentSuccess(response.data));
      resolve(true);
    } catch (error) {
      if (error.response?.status === 404) {
        dispatch(forceShowTosConsent());
        resolve(false);
      } else {
        dispatch(fetchUserTosConsentFailure(error));
        reject();
      }
    }
  });
};

const updateUserTosConsentRequest = () =>
  ({
    type: 'UPDATE_TOS_CONSENT_REQUEST'
  } as const);

const updateUserTosConsentSuccess = () =>
  ({
    type: 'UPDATE_TOS_CONSENT_SUCCESS'
  } as const);

const updateUserTosConsentFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: 'UPDATE_TOS_CONSENT_FAILURE',
    error
  } as const;
};

export const updateUserTosConsent = (
  organizationId?: string
): ThunkAction<Promise<ITosConsent>, CommonRootState, null, Actions> => async dispatch => {
  return new Promise(async (resolve, reject) => {
    try {
      dispatch(updateUserTosConsentRequest());

      await axios.post<ITosConsent>(`/api/v1/OrganizationTermsOfService/${organizationId}`);

      dispatch(updateUserTosConsentSuccess());
      dispatch(fetchUserTosConsent(organizationId));
      resolve();
    } catch (error) {
      dispatch(updateUserTosConsentFailure(error));
      reject(error);
    }
  });
};

export type Actions =
  | ReturnType<typeof fetchUserEulaConsentRequest>
  | ReturnType<typeof fetchUserEulaConsentSuccess>
  | ReturnType<typeof fetchUserEulaConsentFailure>
  | ReturnType<typeof fetchUserTosConsentRequest>
  | ReturnType<typeof fetchUserTosConsentSuccess>
  | ReturnType<typeof fetchUserTosConsentFailure>
  | ReturnType<typeof updateUserTosConsentRequest>
  | ReturnType<typeof updateUserTosConsentSuccess>
  | ReturnType<typeof updateUserTosConsentFailure>
  | ReturnType<typeof forceShowTosConsent>;
